import React from 'react';
import AuthenticatedInput from '../AuthenticatedInput';
import { updateExamField } from '../../utils/examData';
import { useExamTab } from './ExamTabContext';
import {
  calculateAnklePlantarFlexion, calculateAnkleDorsiflexion,
  calculateAnkleInversion, calculateAnkleEversion,
} from '../TabRatingNarratives';

const AnklesTab: React.FC = () => {
  const {
    examData, setExamData, user, userProfile, startManualSession, setShowAuthModal,
    copyRegionValues, handleClearRegion, clearConfirmRegion, fillNormalValues,
  } = useExamTab();

  return (
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Ankles</h3>

                  <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start">

                  {/* Ankle WPI Summary Section — live WPI instrument (right column) */}
                  {examData && (
                    <div className="lg:order-2 lg:sticky lg:top-20">
                      <div className="card p-4 flex flex-col gap-4">
                        <div>
                          <span className="font-medium">Right Ankle WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                            {(() => {
                              // Calculate right ankle impairment
                              const plantarFlexion = examData.ankles.right_plantar_flexion;
                              const dorsiflexion = examData.ankles.right_dorsiflexion;
                              const inversion = examData.ankles.right_inversion;
                              const eversion = examData.ankles.right_eversion;
                              
                              // Check if right ankle has any ROM values
                              if (plantarFlexion === null && 
                                  dorsiflexion === null && 
                                  inversion === null && 
                                  eversion === null) {
                                return "--";
                              }
                              
                              // Sum the motions in % lower extremity, then convert LE -> WPI
                              const joint_sum = calculateAnklePlantarFlexion(plantarFlexion) +
                                                calculateAnkleDorsiflexion(dorsiflexion) +
                                                calculateAnkleInversion(inversion) +
                                                calculateAnkleEversion(eversion);
                              const le_to_wpi = Math.round(joint_sum * 0.4);
                              
                              return le_to_wpi + '%';
                            })()}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">Left Ankle WPI:</span>
                          <span className="ml-2 text-xl font-bold">
                            {(() => {
                              // Calculate left ankle impairment
                              const plantarFlexion = examData.ankles.left_plantar_flexion;
                              const dorsiflexion = examData.ankles.left_dorsiflexion;
                              const inversion = examData.ankles.left_inversion;
                              const eversion = examData.ankles.left_eversion;
                              
                              // Check if left ankle has any ROM values
                              if (plantarFlexion === null && 
                                  dorsiflexion === null && 
                                  inversion === null && 
                                  eversion === null) {
                                return "--";
                              }
                              
                              // Sum the motions in % lower extremity, then convert LE -> WPI
                              const joint_sum = calculateAnklePlantarFlexion(plantarFlexion) +
                                                calculateAnkleDorsiflexion(dorsiflexion) +
                                                calculateAnkleInversion(inversion) +
                                                calculateAnkleEversion(eversion);
                              const le_to_wpi = Math.round(joint_sum * 0.4);
                              
                              return le_to_wpi + '%';
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Range of Motion Section — left column */}
                  <div className="lg:order-1 mb-0">
                    <div className="flex justify-between items-center mb-4 border-b border-border pb-2">
                      <h4 className="text-lg font-medium">Range of Motion</h4>
                      <div className="flex gap-2 flex-wrap"><button onClick={() => copyRegionValues('ankles', 'right_to_left')} className="btn-secondary text-xs" title="Copy Right values to Left">R → L</button><button onClick={() => copyRegionValues('ankles', 'left_to_right')} className="btn-secondary text-xs" title="Copy Left values to Right">L → R</button><button onClick={() => handleClearRegion('ankles')} className={clearConfirmRegion === 'ankles' ? 'btn-danger text-xs' : 'btn-secondary text-xs'}>{clearConfirmRegion === 'ankles' ? 'Confirm Clear?' : 'Clear Values'}</button><button onClick={() => fillNormalValues('ankles')} className="btn-secondary text-xs" disabled={!user}>Fill Normal Values</button></div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-4 gap-y-4">
                      {/* Header Row */}
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-center text-xs font-medium text-content-tertiary hidden sm:block">Normal</div>
                      <div className="col-span-1 text-center font-medium">Right</div>
                      <div className="col-span-1 text-center font-medium">Left</div>

                      {/* Plantar Flexion Row */}
                      <div className="col-span-1 text-content-secondary">Plantar Flexion</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">40°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.ankles?.right_plantar_flexion ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              ankles: {
                                ...prev.ankles,
                                right_plantar_flexion: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.ankles?.left_plantar_flexion ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              ankles: {
                                ...prev.ankles,
                                left_plantar_flexion: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      
                      {/* Dorsiflexion Row */}
                      <div className="col-span-1 text-content-secondary">Dorsiflexion</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">20°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.ankles?.right_dorsiflexion ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              ankles: {
                                ...prev.ankles,
                                right_dorsiflexion: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.ankles?.left_dorsiflexion ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              ankles: {
                                ...prev.ankles,
                                left_dorsiflexion: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                  
                      {/* Inversion Row */}
                      <div className="col-span-1 text-content-secondary">Inversion</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">30°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.ankles?.right_inversion ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              ankles: {
                                ...prev.ankles,
                                right_inversion: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.ankles?.left_inversion ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              ankles: {
                                ...prev.ankles,
                                left_inversion: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      
                      {/* Eversion Row */}
                      <div className="col-span-1 text-content-secondary">Eversion</div>
                      <div className="col-span-1 text-center text-content-tertiary text-sm hidden sm:block">20°</div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.ankles?.right_eversion ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              ankles: {
                                ...prev.ankles,
                                right_eversion: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>
                      <div className="col-span-1">
                        <AuthenticatedInput
                          type="number"
                          className="input-field text-center"
                          value={examData?.ankles?.left_eversion ?? ''}
                          onChange={(value) => {
                            const numValue = value === '' || value === null ? null : Number(value);
                            setExamData(prev => prev ? {
                              ...prev,
                              ankles: {
                                ...prev.ankles,
                                left_eversion: numValue
                              }
                            } : null);
                          }}
                          user={user}
                          userProfile={userProfile}
                          onSessionStart={startManualSession}
                          onShowAuthModal={() => setShowAuthModal(true)}
                        />
                      </div>

                      {/* No separate Subtalar Inversion/Eversion rows: in Table 17-12 hindfoot
                          inversion/eversion ARE the subtalar motions (rated above). The extra rows
                          were never rated and the parser never fills them, so typed values were lost. */}
                    </div>
                  </div>
                  

                  </div>
                </div>
  );
};

export default AnklesTab;
